import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as X } from '../../../../assets/icons/close-icon.svg';
import { ReactComponent as MapPin } from '../../../../assets/icons/location-icon.svg';
import { ReactComponent as Sync } from '../../../../assets/icons/sync.svg';
import { ReactComponent as Upload } from '../../../../assets/icons/upload-icon.svg';
import { ReactComponent as Zap } from '../../../../assets/icons/zap.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { ApiRequest } from '../../../../interface/common.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  broadcastCpoLocations,
  fetchCpoLocationFilters,
  fetchCpoLocations,
  setSortData,
  syncCpoLocations,
  updateCPOLocationsFilters,
} from '../../../../redux/slices/OCPI/CPOLocationsSlice';
import { RootState } from '../../../../redux/store';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
// import TabMenus from '../../../../shared-components/tab-menus/TabMenus';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import chargingStationApi from '../../../../utils/api/charging-station';

// const syncModeTabs = ['AUTOMATIC', 'MANUAL'] as const;

/**
 * Builds a user-facing error message for a failed charging-station sync request,
 * distinguishing between being offline, no response from the server, and a server error.
 * @param {any} error - The error thrown by the sync request.
 * @returns {string} A user-facing error message.
 */
const resolveSyncDataErrorMessage = (error: any): string => {
  if (typeof window !== 'undefined' && !window.navigator.onLine) {
    return 'You appear to be offline. Check your internet connection and try again.';
  }
  if (error?.response) {
    return (
      error.response.data?.message ||
      error.response.data?.status_message ||
      'Failed to sync charging station data.'
    );
  }
  if (error?.request) {
    return 'No response received from the server. Please try again later.';
  }
  return error?.message || 'Failed to sync charging station data.';
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface OcpiEvse {
  uid: string;
  evse_id?: string;
  status: string;
  connectors?: any[];
  last_updated?: string;
}

interface OcpiLocation {
  id: string;
  name?: string;
  address?: string;
  city?: string;
  country?: string;
  country_code?: string;
  party_id?: string;
  publish?: boolean;
  coordinates?: { latitude: string; longitude: string };
  latitude?: number;
  longitude?: number;
  evses?: OcpiEvse[];
  last_updated?: string;
  postal_code?: string;
  parking_type?: string;
  time_zone?: string;
  opening_times?: { twentyfourseven?: boolean; regular_hours?: any[] };
  _objectId?: string;
  _lastUpdated?: string;
  _createdAt?: string;
  createdAt?: string;
  created_at?: string;
  created?: string;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

/**
 * Renders a status badge for an EVSE.
 * @param {any} props The props.
 * @param {any} props.status The status.
 */
function StatusBadge({ status }: { status: string }) {
  let modifier = 'cpo-status-badge--pending';
  if (status === 'AVAILABLE') {
    modifier = 'cpo-status-badge--available';
  } else if (status === 'CHARGING') {
    modifier = 'cpo-status-badge--charging';
  } else if (status === 'BLOCKED' || status === 'FAULTED') {
    modifier = 'cpo-status-badge--blocked';
  }

  return <span className={`cpo-status-badge ${modifier}`}>{status}</span>;
}

// ── EVSE Drill-Down Modal ─────────────────────────────────────────────────────

/**
 * Renders the EVSE drill-down modal.
 * @param {any} props The props.
 * @param {any} props.location The location details.
 * @param {any} props.onClose The close callback.
 */
function LocationDrillDownModal({
  location,
  onClose,
}: Readonly<{
  location: OcpiLocation;
  onClose: () => void;
}>) {
  const evses: OcpiEvse[] = location.evses || [];

  return (
    <ModalBox
      open
      header={false}
      notification
      title={null}
      closeModal={onClose}
      content={
        <div className="cpo-drilldown-modal">
          {/* Header */}
          <div className="cpo-drilldown-header">
            <div>
              <h3 className="cpo-drilldown-title">{location.name || 'Location'}</h3>
              <p className="cpo-drilldown-subtitle">
                <MapPin width={12} height={12} />
                {location.address}
                {location.city ? `, ${location.city}` : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="cpo-drilldown-close">
              <X height={30} width={30} />
            </button>
          </div>

          {/* EVSE List */}
          <div className="cpo-drilldown-body">
            {evses.length > 0 && (
              <div className="cpo-drilldown-select-all-row">
                <span className="cpo-drilldown-select-all-count">
                  {evses.length} EVSE{evses.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}

            {evses.length === 0 ? (
              <div className="cpo-drilldown-empty">
                <Zap height={32} width={32} className="cpo-drilldown-empty-icon" />
                <p>No EVSEs found for this location.</p>
                <p>Sync from CPO to populate charger data.</p>
              </div>
            ) : (
              <div className="cpo-evse-list">
                {evses.map((evse) => (
                  <div key={evse.uid} className="cpo-evse-row">
                    <div className="cpo-evse-content">
                      <div className="cpo-evse-head">
                        <span className="cpo-evse-id">{evse.evse_id || evse.uid}</span>
                        <StatusBadge status={evse.status || 'UNKNOWN'} />
                      </div>
                      <div className="cpo-evse-sub">
                        {evse.connectors?.length || 0} connector
                        {(evse.connectors?.length || 0) !== 1 ? 's' : ''}
                        {evse.connectors?.[0]?.standard
                          ? ` · ${evse.connectors[0].standard}`
                          : ''}
                        {evse.connectors?.[0]?.power_type
                          ? ` · ${evse.connectors[0].power_type}`
                          : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

/**
 * CpoLocationsPanel component for managing CPO OCPI locations.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 */
export default function CpoLocationsPanel({
  countryCode,
  partyId,
}: Readonly<{
  countryCode: string;
  partyId: string;
}>) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['ocpi']);
  const {
    locations,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
  } = useAppSelector((state: RootState) => state.cpoLocations);
  const [syncing, setSyncing] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [syncingData, setSyncingData] = useState(false);
  const [syncDataSuccessMessage, setSyncDataSuccessMessage] = useState<string | null>(null);
  // const [syncMode, setSyncMode] = useState<'AUTOMATIC' | 'MANUAL'>('AUTOMATIC');
  const [selectedLocation, setSelectedLocation] = useState<OcpiLocation | null>(null);
  const hasSynced = useRef(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Synchronizes CPO locations from the CPO backend.
   */
  const handleSync = useCallback(async () => {
    setSyncing(true);
    hasSynced.current = true;
    try {
      await dispatch(syncCpoLocations({ countryCode, partyId })).unwrap();
    } catch {
      // Notification already dispatched by the syncCpoLocations thunk.
    } finally {
      setSyncing(false);
    }
  }, [countryCode, partyId, dispatch]);

  useEffect(() => {
    handleSync();
  }, [countryCode, partyId, handleSync]);

  /**
   * Broadcasts all CPO locations to connected partners.
   */
  const handleBroadcast = async () => {
    setBroadcasting(true);
    try {
      await dispatch(broadcastCpoLocations({ countryCode, partyId })).unwrap();
    } catch {
      // Notification already dispatched by the broadcastCpoLocations thunk.
    } finally {
      setBroadcasting(false);
    }
  };

  interface LocationRow {
    id: string;
    name: string;
    address: string;
    city: string;
    postalCode: string;
    partyId: string;
    countryCode: string;
    coordinates: string;
    parkingType: string;
    timeZone: string;
    open247: boolean;
    published: boolean;
    evsesCount: number;
    lastUpdated: string;
    _createdAt?: string;
    createdAt: string;
    raw: OcpiLocation;
  }

  const columns = [
    {
      label: t('ocpi:created'),
      accessorKey: '_createdAt' as keyof LocationRow,
      sortAllow: false,
      /**
       * Rendering created date
       * @param {LocationRow} currentRow The current location row.
       * @returns {any} The rendered created date element.
       */
      customData: (currentRow: LocationRow) => (
        <span>{ConvertUTCtoDateTime(currentRow._createdAt || currentRow.createdAt)}</span>
      ),
    },
    {
      label: t('ocpi:locationId'),
      accessorKey: 'id' as keyof LocationRow,
      sortAllow: false,
      columnWidth: 260,
      /**
       * Renders the location ID in a code badge.
       * @param {any} row The location row.
       */
      customData: (row: LocationRow) => <code className="cpo-location-id-badge">{row.id}</code>,
    },
    {
      label: t('ocpi:name'),
      accessorKey: 'name' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:address'),
      accessorKey: 'address' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:city'),
      accessorKey: 'city' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:postalCode'),
      accessorKey: 'postalCode' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:partyId'),
      accessorKey: 'partyId' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:countryCode'),
      accessorKey: 'countryCode' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:coordinates'),
      accessorKey: 'coordinates' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:parkingType'),
      accessorKey: 'parkingType' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:timeZone'),
      accessorKey: 'timeZone' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:open247'),
      accessorKey: 'open247' as keyof LocationRow,
      sortAllow: false,
      /**
       * Renders the 24/7 open status badge.
       * @param {LocationRow} row The location row.
       */
      customData: (row: LocationRow) => (
        <span
          className={`cpo-published-badge ${
            row.open247 ? 'cpo-published-badge--yes' : 'cpo-published-badge--no'
          }`}>
          {row.open247 ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      label: t('ocpi:published').toUpperCase(),
      accessorKey: 'published' as keyof LocationRow,
      sortAllow: false,
      /**
       * Renders the published status badge.
       * @param {any} row The location row.
       */
      customData: (row: LocationRow) => (
        <span
          className={`cpo-published-badge ${
            row.published ? 'cpo-published-badge--yes' : 'cpo-published-badge--no'
          }`}>
          {row.published ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      label: t('ocpi:evses').toUpperCase(),
      accessorKey: 'evsesCount' as keyof LocationRow,
      sortAllow: false,
      /**
       * Renders the EVSEs count badge.
       * @param {any} row The location row.
       */
      customData: (row: LocationRow) => (
        <span className="cpo-evses-count-badge">
          {row.evsesCount} EVSE{row.evsesCount !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      label: t('ocpi:created'),
      accessorKey: 'createdAt' as keyof LocationRow,
      sortAllow: false,
    },
    {
      label: t('ocpi:lastUpdated').toUpperCase(),
      accessorKey: 'lastUpdated' as keyof LocationRow,
      sortAllow: false,
    },
  ];

  const tableData: LocationRow[] = locations.map((loc: OcpiLocation) => {
    const dateString = loc.last_updated || loc._lastUpdated;
    const lastUpdated = dateString ? new Date(dateString).toLocaleDateString() : '–';
    const { coordinates } = loc;
    const createdAt =
      loc._createdAt || loc.createdAt || (loc as any).created_at || (loc as any).created || '';
    return {
      id: loc.id,
      name: loc.name || '–',
      address: loc.address || '–',
      city: loc.city || '–',
      postalCode: loc.postal_code || '–',
      partyId: loc.party_id || '–',
      countryCode: loc.country_code || '–',
      coordinates: coordinates ? `${coordinates.latitude}, ${coordinates.longitude}` : '–',
      parkingType: loc.parking_type || '–',
      timeZone: loc.time_zone || '–',
      open247: Boolean(loc.opening_times?.twentyfourseven),
      published: Boolean(loc.publish),
      evsesCount: loc.evses?.length ?? 0,
      lastUpdated,
      _createdAt: createdAt,
      createdAt,
      raw: loc,
    };
  });

  /**
   * Fetches CPO locations for this role with the given pagination/sort parameters.
   * @param {any} data - The pagination/sort parameters.
   */
  const fetchData = (data: ApiRequest) => {
    if (!loading) {
      dispatch(fetchCpoLocations({ countryCode, partyId, ...data }));
    }
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageAndLimitChange = (value: ApiRequest) => {
    fetchData({ ...value, sortField, sortBy });
  };

  // /**
  //  * Refreshes the current page of CPO locations.
  //  */
  // const handleRefresh = () => {
  //   fetchData({ page, limit, sortField, sortBy });
  // };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: { sortField: string; sortBy: 'asc' | 'desc' }) => {
    dispatch(setSortData(sortData));
    fetchData({
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Manually syncs charging station data with the OCPI gateway. Shows a success modal
   * and refreshes the locations list on success; errors (offline, no response, or a
   * server-side failure) are surfaced via the shared notification toast.
   */
  const handleSyncData = async () => {
    setSyncingData(true);
    try {
      const response = await chargingStationApi.syncOcpiChargingStations();
      const message =
        response?.data?.message ||
        response?.data?.status_message ||
        'Charging station data synced successfully.';
      setSyncDataSuccessMessage(message);
      handleRefresh();
    } catch (error: any) {
      dispatch(
        setNotificationMessage({ message: resolveSyncDataErrorMessage(error), type: 'error' }),
      );
    } finally {
      setSyncingData(false);
    }
  };

  return (
    <div className="cpo-locations-panel">
      {/* ── Top bar ── */}
      <div className="cpo-locations-topbar">
        <div>
          <h3 className="cpo-panel-title">OCPI Locations</h3>
          <p className="cpo-panel-subtitle">
            {locations.length > 0
              ? `${locations.length} location${locations.length !== 1 ? 's' : ''} in OCPI gateway`
              : 'Sync to load CPO locations into the OCPI gateway'}
          </p>
        </div>

        {/* Sync mode toggle */}
        {/* <div className="cpo-syncmode-toggle">
          <span className="cpo-syncmode-label">Push Mode:</span>
          <TabMenus
            openTab={syncModeTabs.indexOf(syncMode)}
            setOpenTab={(index) => setSyncMode(syncModeTabs[index])}
            menus={syncModeTabs.map((mode) => ({
              name: mode,
              icon: mode === 'AUTOMATIC' ? <Wifi size={12} /> : <WifiOff size={12} />,
              activeIcon: mode === 'AUTOMATIC' ? <Wifi size={12} /> : <WifiOff size={12} />,
            }))}
          />
        </div> */}
      </div>

      {/* ── Locations Table ── */}
      <CustomTable
        version="v2"
        header={columns}
        data={tableData}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading || syncing}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        filters={filters}
        filtersToDispatch={updateCPOLocationsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchCpoLocationFilters}
        sizeChangeButtonRequired
        actionDropdown
        rowClickEvent={(_id, row) => setSelectedLocation(row.raw)}
        rowClickParamsKey="id"
        refershCurrentPages={handleRefresh}
        requiredRefreshToggle
        refreshFunction={handleRefresh}
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel="Refresh"
        rightTopContent={
          // syncMode === 'AUTOMATIC' ? (
          <div className="cpo-locations-actions">
            <MyButton
              label={syncingData ? 'Syncing…' : 'Sync Data'}
              svgIcon={<Sync />}
              buttonType="outline"
              onClickFunc={handleSyncData}
              disableButton={syncingData}
            />
            <MyButton
              buttonType="primary"
              label={broadcasting ? 'Broadcasting…' : 'Broadcast All'}
              svgIcon={<Upload />}
              onClickFunc={handleBroadcast}
              disableButton={broadcasting || locations.length === 0}
            />
          </div>
          // ) : undefined
        }
      />

      {/* ── Drill-down modal ── */}
      {selectedLocation && (
        <LocationDrillDownModal
          location={selectedLocation}
          onClose={() => setSelectedLocation(null)}
        />
      )}

      {/* ── Sync data success modal ── */}
      <ModalBox
        open={Boolean(syncDataSuccessMessage)}
        header={false}
        notification
        title={null}
        closeModal={() => setSyncDataSuccessMessage(null)}
        content={
          <SuccessNotification
            title="Charging Station Data Synced"
            description={syncDataSuccessMessage || ''}
            onNotificationClose={() => setSyncDataSuccessMessage(null)}
          />
        }
      />
    </div>
  );
}
